import { useEffect, useState } from "react";
import { confirm } from "../components/ConfirmDialog";
import { apiClient } from "../App";
import AdminLayout from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Sparkles, Plus, Trash2, Pencil, Rocket, RotateCcw, EyeOff,
} from "lucide-react";
import { toast } from "sonner";
import { ROADMAP_ICON_OPTIONS, RoadmapIcon } from "../lib/roadmapIcons";

const STATUS_META = {
  coming_soon: { label: "Coming Soon", color: "bg-amber-50 text-amber-700 border-amber-200" },
  launched:    { label: "Launched",    color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  hidden:      { label: "Hidden",      color: "bg-slate-100 text-slate-500 border-slate-200" },
};

const emptyForm = { title: "", tagline: "", icon: "sparkles", eta: "", order: 100 };

export default function AdminRoadmap() {
  const [items, setItems] = useState([]);
  const [windowDays, setWindowDays] = useState(14);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchItems = async () => {
    try {
      const res = await apiClient.get("/admin/roadmap/items");
      setItems(res.data.items || []);
      setWindowDays(res.data.just_launched_window_days || 14);
    } catch (err) {
      toast.error("Failed to load roadmap items");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, order: (items.length + 1) * 10 });
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      title: item.title || "",
      tagline: item.tagline || "",
      icon: item.icon || "sparkles",
      eta: item.eta || "",
      order: item.order ?? 100,
    });
    setDialogOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      if (editing) {
        await apiClient.patch(`/admin/roadmap/items/${editing.roadmap_item_id}`, form);
        toast.success("Roadmap item updated");
      } else {
        await apiClient.post("/admin/roadmap/items", form);
        toast.success("Roadmap item created");
      }
      setDialogOpen(false);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const transitionStatus = async (item, status) => {
    try {
      await apiClient.patch(`/admin/roadmap/items/${item.roadmap_item_id}`, { status });
      toast.success(`Marked as ${STATUS_META[status]?.label || status}`);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  const remove = async (item) => {
    const ok = await confirm({
      title: `Delete "${item.title}"?`,
      description: "This removes the roadmap item from every tenant's roadmap view. Any upvotes or comments tied to it are lost. This cannot be undone.",
      confirmText: "Delete item",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await apiClient.delete(`/admin/roadmap/items/${item.roadmap_item_id}`);
      toast.success("Deleted");
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <AdminLayout>
      <div className="p-6 max-w-6xl space-y-6" data-testid="admin-roadmap-page">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-[#0A192F] font-['Manrope'] tracking-tight flex items-center gap-2">
              <Sparkles className="w-7 h-7 text-amber-500" /> Roadmap
            </h1>
            <p className="text-slate-600 mt-1">
              Control the &ldquo;Coming Soon&rdquo; and &ldquo;Just Launched&rdquo; announcements shown to all tenants.
              Launched items auto-hide from customers after {windowDays} days.
            </p>
          </div>
          <Button
            onClick={openCreate}
            className="bg-[#0D9488] hover:bg-[#14B8A6]"
            data-testid="admin-roadmap-add-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Item
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">All Items</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-14 bg-slate-100 rounded animate-pulse" />
                ))}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">Icon</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>ETA</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-16 text-right">Order</TableHead>
                    <TableHead className="w-64 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody data-testid="admin-roadmap-table-body">
                  {items.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-slate-500 py-8">
                        No roadmap items yet. Click <em>Add Item</em> to create one.
                      </TableCell>
                    </TableRow>
                  )}
                  {items.map((item) => {
                    const meta = STATUS_META[item.status] || STATUS_META.coming_soon;
                    return (
                      <TableRow key={item.roadmap_item_id} data-testid={`admin-roadmap-row-${item.roadmap_item_id}`}>
                        <TableCell><RoadmapIcon iconKey={item.icon} /></TableCell>
                        <TableCell>
                          <div className="font-semibold text-[#0A192F]">{item.title}</div>
                          <div className="text-xs text-slate-500 line-clamp-1">{item.tagline}</div>
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">{item.eta || "—"}</TableCell>
                        <TableCell>
                          <span className={`text-xs font-semibold uppercase tracking-wider px-2 py-0.5 rounded border ${meta.color}`}>
                            {meta.label}
                          </span>
                        </TableCell>
                        <TableCell className="text-right text-sm text-slate-500">{item.order}</TableCell>
                        <TableCell>
                          <div className="flex justify-end gap-1">
                            <Button size="sm" variant="ghost" onClick={() => openEdit(item)}
                              data-testid={`admin-roadmap-edit-${item.roadmap_item_id}`}
                              className="text-slate-500 hover:text-[#0D9488]" aria-label="Edit">
                              <Pencil className="w-4 h-4" />
                            </Button>
                            {item.status !== "launched" && (
                              <Button size="sm" variant="ghost"
                                onClick={() => transitionStatus(item, "launched")}
                                data-testid={`admin-roadmap-launch-${item.roadmap_item_id}`}
                                className="text-emerald-600 hover:text-emerald-700" aria-label="Mark launched">
                                <Rocket className="w-4 h-4" />
                              </Button>
                            )}
                            {item.status !== "coming_soon" && (
                              <Button size="sm" variant="ghost"
                                onClick={() => transitionStatus(item, "coming_soon")}
                                data-testid={`admin-roadmap-restore-${item.roadmap_item_id}`}
                                className="text-amber-600 hover:text-amber-700" aria-label="Restore to Coming Soon">
                                <RotateCcw className="w-4 h-4" />
                              </Button>
                            )}
                            {item.status !== "hidden" && (
                              <Button size="sm" variant="ghost"
                                onClick={() => transitionStatus(item, "hidden")}
                                data-testid={`admin-roadmap-hide-${item.roadmap_item_id}`}
                                className="text-slate-500 hover:text-slate-700" aria-label="Hide">
                                <EyeOff className="w-4 h-4" />
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" onClick={() => remove(item)}
                              data-testid={`admin-roadmap-delete-${item.roadmap_item_id}`}
                              className="text-rose-500 hover:text-rose-700" aria-label="Delete">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="admin-roadmap-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Roadmap Item" : "New Roadmap Item"}</DialogTitle>
            <DialogDescription>
              {editing ? "Update how this item appears in the sidebar and Roadmap tab." : "Create a new upcoming-feature announcement for all tenants."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div>
              <Label htmlFor="rm-title">Title</Label>
              <Input id="rm-title" data-testid="admin-roadmap-title-input"
                required maxLength={140} value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Payroll Integration" />
            </div>
            <div>
              <Label htmlFor="rm-tagline">Tagline</Label>
              <Textarea id="rm-tagline" data-testid="admin-roadmap-tagline-input"
                maxLength={280} rows={2} value={form.tagline}
                onChange={(e) => setForm({ ...form, tagline: e.target.value })}
                placeholder="One-line pitch shown in the card." />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Icon</Label>
                <Select value={form.icon} onValueChange={(v) => setForm({ ...form, icon: v })}>
                  <SelectTrigger data-testid="admin-roadmap-icon-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROADMAP_ICON_OPTIONS.map((opt) => (
                      <SelectItem key={opt.key} value={opt.key}>
                        <span className="flex items-center gap-2">
                          <RoadmapIcon iconKey={opt.key} className="w-4 h-4" />
                          {opt.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="rm-eta">ETA</Label>
                <Input id="rm-eta" data-testid="admin-roadmap-eta-input"
                  maxLength={32} value={form.eta}
                  onChange={(e) => setForm({ ...form, eta: e.target.value })}
                  placeholder="Q2 2026" />
              </div>
            </div>
            <div>
              <Label htmlFor="rm-order">Sort Order</Label>
              <Input id="rm-order" data-testid="admin-roadmap-order-input"
                type="number" value={form.order}
                onChange={(e) => setForm({ ...form, order: Number(e.target.value) || 0 })} />
              <p className="text-xs text-slate-500 mt-1">Lower numbers appear first.</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving || !form.title.trim()}
                data-testid="admin-roadmap-save-btn"
                className="bg-[#0D9488] hover:bg-[#14B8A6]">
                {saving ? "Saving..." : editing ? "Save Changes" : "Create Item"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
